import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  Truck,
  Anchor,
  Airplane,
  Globe,
  ShieldCheck,
  ChartLineUp,
  Users,
  Lightning,
  ArrowRight,
  CheckCircle,
  Package,
  CurrencyCircleDollar,
  Clock,
  Handshake,
} from '@phosphor-icons/react'

export default function LandingPage() {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()

  const handleGetStarted = () => {
    if (isAuthenticated && user) {
      if (user.user_master) {
        navigate('/master/dashboard')
      } else if (user.current_company?.type === 'freight-forwarder') {
        navigate('/freight-forwarder/dashboard')
      } else {
        navigate('/shipper/dashboard')
      }
    } else {
      navigate('/signup')
    }
  }

  const handleLogin = () => {
    if (isAuthenticated && user) {
      if (user.user_master) {
        navigate('/master/dashboard')
      } else if (user.current_company?.type === 'freight-forwarder') {
        navigate('/freight-forwarder/dashboard')
      } else {
        navigate('/shipper/dashboard')
      }
    } else {
      navigate('/login')
    }
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header/Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Eximtech" className="h-10 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleLogin}
                className="text-slate-300 hover:text-white transition-colors font-medium"
              >
                Entrar
              </button>
              <button
                onClick={handleGetStarted}
                className="px-4 py-2 rounded-lg text-white font-medium transition-all hover:shadow-lg"
                style={{ backgroundColor: 'rgb(5, 150, 105)' }}
              >
                Começar Grátis
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, rgb(37, 48, 55) 0%, rgb(15, 23, 42) 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <Lightning size={16} weight="fill" className="text-emerald-400" />
                <span className="text-sm text-emerald-400 font-medium">Plataforma de Comércio Exterior</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Simplifique sua
                <span className="block" style={{ color: 'rgb(5, 150, 105)' }}>logística internacional</span>
              </h1>
              <p className="text-lg text-slate-400 mb-8 max-w-lg">
                Conecte-se com os melhores agentes de carga, receba cotações competitivas e gerencie suas operações de importação e exportação em um só lugar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleGetStarted}
                  className="px-6 py-3 rounded-lg text-white font-semibold transition-all hover:shadow-xl flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                >
                  Criar Conta Grátis
                  <ArrowRight size={20} weight="bold" />
                </button>
                <button
                  onClick={handleLogin}
                  className="px-6 py-3 rounded-lg text-white font-semibold bg-slate-700 hover:bg-slate-600 transition-all flex items-center justify-center gap-2"
                >
                  Já tenho conta
                </button>
              </div>

              {/* Stats */}
              <div className="flex gap-8 mt-12">
                <div>
                  <div className="text-3xl font-bold text-white">500+</div>
                  <div className="text-sm text-slate-400">Operações realizadas</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">50+</div>
                  <div className="text-sm text-slate-400">Agentes de carga</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">98%</div>
                  <div className="text-sm text-slate-400">Satisfação</div>
                </div>
              </div>
            </div>

            {/* Transport Icons */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-emerald-500/50 transition-all transform hover:-translate-y-1 h-44 flex flex-col justify-center">
                <Anchor size={48} weight="duotone" className="mb-4" style={{ color: 'rgb(5, 150, 105)' }} />
                <h3 className="text-xl font-semibold text-white mb-2">Marítimo</h3>
                <p className="text-slate-400 text-sm">FCL e LCL para todos os portos do mundo</p>
              </div>
              <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-emerald-500/50 transition-all transform hover:-translate-y-1 h-44 flex flex-col justify-center">
                <Airplane size={48} weight="duotone" className="mb-4" style={{ color: 'rgb(5, 150, 105)' }} />
                <h3 className="text-xl font-semibold text-white mb-2">Aéreo</h3>
                <p className="text-slate-400 text-sm">Carga expressa e consolidada</p>
              </div>
              <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-emerald-500/50 transition-all transform hover:-translate-y-1 h-44 flex flex-col justify-center">
                <Truck size={48} weight="duotone" className="mb-4" style={{ color: 'rgb(5, 150, 105)' }} />
                <h3 className="text-xl font-semibold text-white mb-2">Rodoviário</h3>
                <p className="text-slate-400 text-sm">Transporte terrestre integrado</p>
              </div>
              <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-emerald-500/50 transition-all transform hover:-translate-y-1 h-44 flex flex-col justify-center">
                <Globe size={48} weight="duotone" className="mb-4" style={{ color: 'rgb(5, 150, 105)' }} />
                <h3 className="text-xl font-semibold text-white mb-2">Multimodal</h3>
                <p className="text-slate-400 text-sm">Soluções integradas porta a porta</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Tudo que você precisa em um só lugar
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Gerencie suas operações de comércio exterior com ferramentas poderosas e intuitivas
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(5, 150, 105, 0.2)' }}>
                <Package size={24} style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Cotações Simplificadas</h3>
              <p className="text-slate-400">Solicite cotações para múltiplos agentes de carga em minutos. Compare preços e serviços facilmente.</p>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(5, 150, 105, 0.2)' }}>
                <CurrencyCircleDollar size={24} style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Propostas Competitivas</h3>
              <p className="text-slate-400">Receba ofertas de diversos agentes e escolha a melhor opção para sua operação.</p>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(5, 150, 105, 0.2)' }}>
                <Clock size={24} style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Acompanhamento em Tempo Real</h3>
              <p className="text-slate-400">Monitore suas cargas e operações em tempo real através do dashboard intuitivo.</p>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(5, 150, 105, 0.2)' }}>
                <ShieldCheck size={24} style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Segurança Garantida</h3>
              <p className="text-slate-400">Seus dados e operações protegidos com criptografia de ponta a ponta.</p>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(5, 150, 105, 0.2)' }}>
                <ChartLineUp size={24} style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Relatórios e Analytics</h3>
              <p className="text-slate-400">Dados e insights para tomar decisões estratégicas sobre suas operações.</p>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(5, 150, 105, 0.2)' }}>
                <Users size={24} style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Gestão de Equipe</h3>
              <p className="text-slate-400">Convide membros da sua equipe e gerencie permissões de acesso.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Como funciona
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Em poucos passos você conecta sua empresa aos melhores agentes de carga
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
                1
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Crie sua conta</h3>
              <p className="text-slate-400">Cadastre-se gratuitamente e configure o perfil da sua empresa em minutos.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
                2
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Solicite cotações</h3>
              <p className="text-slate-400">Preencha os detalhes da sua carga e receba propostas de múltiplos agentes.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
                3
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Gerencie operações</h3>
              <p className="text-slate-400">Aceite a melhor proposta e acompanhe toda a operação pelo dashboard.</p>
            </div>
          </div>
        </div>
      </section>

      {/* For Who Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* For Shippers */}
            <div className="bg-slate-900/50 rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-6">
                <Package size={32} style={{ color: 'rgb(5, 150, 105)' }} />
                <h3 className="text-2xl font-bold text-white">Para Embarcadores</h3>
              </div>
              <p className="text-slate-400 mb-6">
                Importe ou exporte com mais facilidade. Conecte-se com agentes de carga qualificados e obtenha as melhores condições.
              </p>
              <ul className="space-y-3">
                {[
                  'Cotações rápidas e competitivas',
                  'Múltiplas propostas para comparar',
                  'Acompanhamento de operações em tempo real',
                  'Histórico completo de embarques',
                  'Suporte dedicado'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle size={20} weight="fill" style={{ color: 'rgb(5, 150, 105)' }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* For Freight Forwarders */}
            <div className="bg-slate-900/50 rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-6">
                <Handshake size={32} style={{ color: 'rgb(5, 150, 105)' }} />
                <h3 className="text-2xl font-bold text-white">Para Agentes de Carga</h3>
              </div>
              <p className="text-slate-400 mb-6">
                Expanda seu negócio conectando-se a novos clientes. Receba oportunidades qualificadas e aumente sua carteira.
              </p>
              <ul className="space-y-3">
                {[
                  'Acesso a novas oportunidades de negócio',
                  'Sistema de propostas integrado',
                  'Gestão de clientes centralizada',
                  'Dashboard de performance',
                  'Sem mensalidade - pague por uso'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle size={20} weight="fill" style={{ color: 'rgb(5, 150, 105)' }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, rgb(5, 150, 105) 0%, rgb(4, 120, 87) 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Pronto para simplificar sua logística?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Junte-se a centenas de empresas que já utilizam o Eximtech para gerenciar suas operações de comércio exterior.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="px-8 py-4 rounded-lg bg-white font-semibold transition-all hover:shadow-xl flex items-center justify-center gap-2"
              style={{ color: 'rgb(5, 150, 105)' }}
            >
              Criar Conta Grátis
              <ArrowRight size={20} weight="bold" />
            </button>
            <button
              onClick={handleLogin}
              className="px-8 py-4 rounded-lg text-white font-semibold border-2 border-white/30 hover:bg-white/10 transition-all flex items-center justify-center gap-2"
            >
              Já tenho conta
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Eximtech" className="h-8 w-auto" />
            </div>
            <p className="text-slate-400 text-sm">
              &copy; {new Date().getFullYear()} Eximtech. Todos os direitos reservados.
            </p>
            <div className="flex gap-6">
              <Link to="/termos" className="text-slate-400 hover:text-white transition-colors text-sm">Termos de Uso</Link>
              <Link to="/privacidade" className="text-slate-400 hover:text-white transition-colors text-sm">Privacidade</Link>
              <a href="mailto:contato@eximtech.com.br" className="text-slate-400 hover:text-white transition-colors text-sm">Contato</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
